import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { PageUrlFilterCodec } from './page-url-filter-codecs';
import { writePageUrlFilterState } from './page-url-filter-codecs';

export type PageUrlFilterStateUpdate<State> =
  | State
  | ((previous: State) => State);

export type SetPageUrlFilterState<State> = (
  update: PageUrlFilterStateUpdate<State>,
  options?: { replace?: boolean }
) => void;

/**
 * Keeps page filter state in the current URL while preserving unrelated query
 * parameters owned by the page (for example selected workflow `id`/`name`).
 */
export function usePageUrlFilterState<State>(
  codec: PageUrlFilterCodec<State>
): readonly [State, SetPageUrlFilterState<State>] {
  const [searchParams, setSearchParams] = useSearchParams();
  const serializedSearchParams = searchParams.toString();
  const state = useMemo(
    () => codec.read(new URLSearchParams(serializedSearchParams)),
    [codec, serializedSearchParams]
  );

  const setState = useCallback<SetPageUrlFilterState<State>>(
    (update, options) => {
      const previous = codec.read(searchParams);
      const nextState =
        typeof update === 'function'
          ? (update as (current: State) => State)(previous)
          : update;
      setSearchParams(writePageUrlFilterState(searchParams, nextState, codec), {
        replace: options?.replace === true,
      });
    },
    [codec, searchParams, setSearchParams]
  );

  return [state, setState] as const;
}
